import * as XLSX from 'xlsx';

import { ProjectData, Task } from 'types/Gantt';

import { assignDynamicColors } from './color';
import { excelDateToJS, formatExcelDate } from './date';

const HEADER_ROW_INDEX = 8;
const headerMapping: Record<string, number> = {};

const sanitiseText = (str: string): string => {
  if (!str) return '';
  return str
    .replace(/[\u2011\u2010]/g, '-')
    .replace(/[\u2013\u2014]/g, '-')
    .replace(/[\u2018\u2019]/g, "'")
    .replace(/[\u201c\u201d]/g, '"')
    .replace(/<>/g, '-');
};

const parseDependencies = (str: string): number[] => {
  if (!str) return [];
  return String(str)
    .split(',')
    .map((s) => {
      const match = s.trim().match(/^(\d+)/);
      return match ? parseInt(match[1], 10) : null;
    })
    .filter((n): n is number => n !== null);
};

const isSummaryTask = (outline: string): boolean => {
  return !outline.includes('.') || /^\d+\.\d+$/.test(outline);
};

export function parseExcel(arrayBuffer: ArrayBuffer): ProjectData {
  const wb = XLSX.read(arrayBuffer, { type: 'array', cellDates: true });
  const ws = wb.Sheets[wb.SheetNames[0]];
  const rows = XLSX.utils.sheet_to_json<any[]>(ws, { header: 1, raw: false, dateNF: 'yyyy-mm-dd' });
  const rawRows = XLSX.utils.sheet_to_json<any[]>(ws, { header: 1, raw: true });

  // 1. Strict Format Validation
  let headerIdx = -1;
  for (let i = 0; i < Math.min(rows.length, 15); i++) {
    const colA = String(rows[i]?.[0] || '')
      .toLowerCase()
      .trim();
    if (colA === 'task number' || colA === 'id') {
      headerIdx = i;
      break;
    }
  }

  if (headerIdx === -1) {
    throw new Error(
      "Invalid file format. Could not find the expected header row (must contain 'Task number' or 'ID' in Column A). Please ensure you are using the specific Planner/Project export template.",
    );
  }

  // Project metadata
  const projectName = String(rows[0]?.[1] || 'Project Timeline');
  const planOwner = String(rows[1]?.[1] || '');
  const projectStart = excelDateToJS(rawRows[2]?.[1]);
  const projectFinish = excelDateToJS(rawRows[3]?.[1]);
  const durationStr = String(rows[4]?.[1] || '');

  const rawTasks: Partial<Task>[] = [];
  for (let item = 0; rows[HEADER_ROW_INDEX].length > item; item++) {
    headerMapping[rows[HEADER_ROW_INDEX][item]] = item;
  }

  // Parse tasks
  for (let i = headerIdx + 1; i < rawRows.length; i++) {
    const raw = rawRows[i];
    if (!raw || raw[0] === undefined || raw[0] === null) continue;

    const taskNum = Number(raw[0]);
    if (isNaN(taskNum)) continue;

    const outlineIndex = headerMapping['Outline number'];
    const nameIndex = headerMapping.Name;
    const startIndex = headerMapping.Start;
    const finishIndex = headerMapping.Finish;
    const durationIndex = headerMapping.Duration;
    const pctCompleteIndex = headerMapping['% complete'];
    const dependsOnIndex = headerMapping['Depends on'];

    const outline = String(raw[outlineIndex] || '');
    const name = sanitiseText(String(raw[nameIndex] || ''));
    const start = excelDateToJS(raw[startIndex]);
    const finish = excelDateToJS(raw[finishIndex]);
    const duration = String(rows[i]?.[durationIndex] || '');
    const pctComplete = Number(raw[pctCompleteIndex]) || 0;
    const dependsOn = String(raw[dependsOnIndex] || '');

    if (!start || !finish) continue;

    const indent = (outline.match(/\./g) || []).length;

    rawTasks.push({
      num: taskNum,
      outline,
      name,
      start,
      finish,
      duration,
      startStr: formatExcelDate(start),
      finishStr: formatExcelDate(finish),
      pctComplete,
      dependsOn,
      indent,
      isSummary: isSummaryTask(outline),
      depRefs: parseDependencies(dependsOn),
    });
  }

  if (rawTasks.length === 0) {
    throw new Error('No tasks with valid start and finish dates were found in the spreadsheet.');
  }

  // Assign dynamic colors based on task hierarchy
  const { coloredTasks, legend } = assignDynamicColors(rawTasks);

  return { projectName, planOwner, projectStart, projectFinish, durationStr, tasks: coloredTasks, legend };
}
